import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FeaturedWorks from '../components/FeaturedWorks.jsx';
import Availability from '../components/Availability.jsx';
import Blurb from '../components/Blurb.jsx';
import LastCommit from '../components/LastCommit.jsx';
import wguFrontEndCert from '../assets/WGU_Certificate_Front_End_Developer.png';

export default function Intro() {
  const workItems = [
    {
      company: 'Surge',
      position: 'Software Engineer',
      duration: '2026-Present',
      achievements: 'Software engineering across team-based projects',
      details: (
        <>
          <p>Contribute software engineering across a range of collaborative, team-based projects.</p>
          <ul className="styled-list">
            <li>Ship features and systems with cross-functional teams</li>
            <li>Support delivery across a variety of project types</li>
          </ul>
        </>
      )
    },
    {
      company: 'Zenimax',
      position: 'Game Developer',
      duration: '2025',
      achievements: 'Leading game development team',
      details: (
        <>
          <p>Own end-to-end gameplay systems, performance passes, and team mentorship.</p>
          <ul className="styled-list">
            <li>Architected core engine modules and tooling</li>
            <li>Improved frame times and asset pipelines</li>
          </ul>
        </>
      )
    },
    {
      company: 'Vylocity',
      position: 'Administration',
      duration: '2020-Present',
      achievements: 'Wrote ~100 pages of documentation, supported devs',
      details: (
        <>
          <p>Built and maintained internal documentation hub; coached contributors.</p>
          <ul className="styled-list">
            <li>Standardized templates and review process</li>
            <li>Reduced onboarding time for new devs</li>
          </ul>
        </>
      )
    },
    {
      company: 'Google Fiber',
      position: 'Software Engineer',
      duration: '2018-2019',
      achievements: 'Code-oriented work and technical support',
      details: (
        <>
          <p>Shipped internal tools and handled escalated technical issues.</p>
          <ul className="styled-list">
            <li>Improved tooling reliability and observability</li>
            <li>Partnered with support to resolve edge cases</li>
          </ul>
        </>
      )
    },
    {
      company: 'BYOND Software',
      position: 'Administration',
      duration: '2015-2019',
      achievements: 'Community moderation and teaching',
      details: (
        <>
          <p>Moderated community channels and delivered learning content.</p>
          <ul className="styled-list">
            <li>Produced tutorials and example projects</li>
            <li>Elevated community health and engagement</li>
          </ul>
        </>
      )
    }
  ];

  const wguFedCredentialUrl =
    'https://wgu.badges.parchment.com/public/assertions/t2MQfH5-QBGhLAwR7eX3gQ?identity__email=mmoel23@wgu.edu';

  const eduItems = [
    {
      title: 'Software Engineering, BS',
      fields: [
        { label: 'University', value: 'Western Governors University' },
        { label: 'Graduation', value: 'Expected 2026' }
      ],
      details: (
        <>
          <p>Focus on software architecture, data structures, and cloud-native systems.</p>
          <ul className="styled-list">
            <li>Projects in fullstack web and mobile</li>
            <li>Emphasis on testing and reliability</li>
          </ul>
        </>
      )
    },
    {
      title: 'Technical Certificates',
      credentialUrl: wguFedCredentialUrl,
      fields: [
        {
          label: 'College',
          value: 'Western Governors University',
          badge: {
            src: wguFrontEndCert,
            alt: 'Western Governors University (WGU) Front-End Developer Certificate badge',
            href: wguFedCredentialUrl
          }
        },
        { label: 'Credential', value: 'Front-End Developer' }
      ],
      details: (
        <>
          <p>
            Industry-recognized credential covering modern front-end development practices,
            tooling, and delivery.
          </p>
          <ul className="styled-list">
            <li>Web foundations, components, and responsive UI</li>
            <li>Accessibility, performance, and quality practices</li>
          </ul>
        </>
      )
    }
  ];

  const [openWorkIndex, setOpenWorkIndex] = useState(null);
  const [openEduSet, setOpenEduSet] = useState(() => new Set());

  return (
    <>
      <h2 className="page-header">Overview</h2>
      <Blurb>
        <p>
          I’m a Software Engineer with a passion for game development and fullstack development.
          This site highlights select work across game development, fullstack web/mobile, and other projects. Hire me!
        </p>
      </Blurb>
      <LastCommit />
      <p className="resume-cta">
        Looking for the brass tacks?
        {' '}<Link to="/resume">Check out my resume</Link>.
      </p>
      <section>
        <h2>Core Skills</h2>
        <ul className="styled-list core-skills">
          <li><b><a href="/game-dev">Game Dev</a></b>: engines, tools, gameplay systems, performance</li>
          <li><b><a href="/fullstack">Fullstack</a></b>: React/React Native, Node/Express, DBs, cloud</li>
          <li><b><a href="/other-dev">Other Dev</a></b>: comics, writing, Minecraft mods, and side projects</li>
          <li><b>Focus</b>: polish, UX, robust architecture, and shipping</li>
        </ul>
      </section>

      <FeaturedWorks category="home" title="Featured Works" />

      <Availability />

      <section className="work-experience">
        <h2>Work Experience</h2>
        <table className="experience-table">
          <caption>Recent Employment History</caption>
          <thead>
            <tr>
              <th>Company</th>
              <th>Position</th>
              <th>Duration</th>
              <th>Key Achievements</th>
            </tr>
          </thead>
          <tbody>
            {workItems.map((item, idx) => (
              <React.Fragment key={`${item.company}-${idx}`}>
                <tr
                  className="work-row clickable"
                  onClick={(e) => {
                    // Avoid double toggle when clicking button inside
                    if ((e.target).closest && (e.target).closest('button')) return;
                    setOpenWorkIndex(openWorkIndex === idx ? null : idx);
                  }}
                >
                  <td>{item.company}</td>
                  <td>{item.position}</td>
                  <td>{item.duration}</td>
                  <td>
                    <div className="row-main">
                      <span>{item.achievements}</span>
                      <button
                        type="button"
                        className="expand-btn"
                        aria-expanded={openWorkIndex === idx}
                        onClick={() => setOpenWorkIndex(openWorkIndex === idx ? null : idx)}
                      >
                        {openWorkIndex === idx ? '- Details' : '+ Details'}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr className="expansion-row" aria-hidden={openWorkIndex !== idx}>
                  <td colSpan={4}>
                    <div className={`expansion-panel ${openWorkIndex === idx ? 'open' : ''}`}>
                      {item.details}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </section>

      <section className="education">
        <h2>Education</h2>
        <div className="education-grid">
          {eduItems.map((edu, idx) => {
            const isOpen = openEduSet.has(idx);
            return (
            <article
              key={`${edu.title}-${idx}`}
              className="clickable"
              onClick={(e) => {
                if (
                  (e.target).closest &&
                  ((e.target).closest('button') || (e.target).closest('a'))
                ) {
                  return;
                }
                setOpenEduSet(prev => {
                  const next = new Set(prev);
                  if (next.has(idx)) next.delete(idx); else next.add(idx);
                  return next;
                });
              }}
            >
              <h3>{edu.title}</h3>
              {edu.fields.map(({ label, value, badge }) => (
                <p
                  key={label}
                  className={badge ? 'education-field-row education-field-with-badge' : undefined}
                >
                  {badge ? (
                    <>
                      <span className="education-field-college-main">
                        <span className="contact-label">{label}:</span>
                        <span>{value}</span>
                      </span>
                      <a
                        href={badge.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="education-cert-link"
                        aria-label="Verify Front-End Developer certificate on Parchment (opens in new tab)"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <img
                          src={badge.src}
                          alt={badge.alt}
                          className="education-cert-img"
                          width={72}
                          height={72}
                        />
                      </a>
                    </>
                  ) : (
                    <>
                      <span className="contact-label">{label}:</span>
                      {' '}
                      {value}
                    </>
                  )}
                </p>
              ))}
              <div className="education-card-actions">
                <button
                  type="button"
                  className="expand-btn"
                  aria-expanded={isOpen}
                  onClick={() => {
                    setOpenEduSet(prev => {
                      const next = new Set(prev);
                      if (next.has(idx)) next.delete(idx); else next.add(idx);
                      return next;
                    });
                  }}
                >
                  {isOpen ? '- Details' : '+ Details'}
                </button>
                {edu.credentialUrl && (
                  <a
                    href={edu.credentialUrl}
                    className="expand-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open credential verification on Parchment (opens in new tab)"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Link
                  </a>
                )}
              </div>
              <div className={`expansion-panel ${isOpen ? 'open' : ''}`}>
                {edu.details}
              </div>
            </article>
          );})}
        </div>
      </section>
    </>
  );
}


